import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { Home } from './pages/public/Home';
import { Rsvp } from './pages/public/Rsvp';
import { RsvpConfirmed } from './pages/public/RsvpConfirmed';

// Phase 2 pages
import Schedule       from "./pages/public/Schedule";
import Gallery        from "./pages/public/Gallery";
import RafflePublic   from "./pages/public/Raffle";
import Golf           from "./pages/public/Golf";
import Maps           from "./pages/public/Maps";
import MyRsvp         from "./pages/MyRsvp";
import AdminDashboard from "./pages/admin/Dashboard";
import EventEditor    from "./pages/admin/EventEditor";
import Raffle         from "./pages/admin/Raffle";
import { EditorGuard, AdminGuard } from "./components/guards/Guards";

function Layout() {
  return (
    <div className="app-shell">
      <Header />
      <main className="app-main">
        <Outlet />
      </main>
      <Footer />
      <style>{`
        .app-shell {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        .app-main { flex: 1; }
      `}</style>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/rsvp" element={<SeasonGuard><Rsvp /></SeasonGuard>} />
          <Route path="/rsvp/confirmed" element={<RsvpConfirmed />} />
          {/* Placeholders for routes we'll build next */}
          <Route path="/schedule" element={<SeasonGuard><Schedule /></SeasonGuard>} />
          <Route path="/gallery"  element={<Gallery />} />
          <Route path="/raffle"   element={<SeasonGuard><RafflePublic /></SeasonGuard>} />
          <Route path="/golf"     element={<SeasonGuard><Golf /></SeasonGuard>} />
          <Route path="/maps"     element={<SeasonGuard><Maps /></SeasonGuard>} />
          <Route path="/my-rsvp"  element={<SeasonGuard><MyRsvp /></SeasonGuard>} />
          {/* EditorGuard, not AdminGuard (issue #12) — the backend allows
              Editor on these pages' endpoints (field-level Admin gating in
              event.ts/config.ts; delete + photo moderation stay Admin-only). */}
          <Route path="/admin" element={<EditorGuard><AdminDashboard /></EditorGuard>} />
          {/* Deep link target of the photo moderation alert email (issue #45) —
              opens the dashboard with the Photos tab active. */}
          <Route path="/admin/photos" element={<EditorGuard><AdminDashboard initialTab="photos" /></EditorGuard>} />
        <Route
          path="/admin/event"
          element={
            <EditorGuard>
              <EventEditor />
            </EditorGuard>
          }
        />
          {/* Raffle is Admin-only (issue #16) — "Editor: No raffle" per the
              Entra role model, enforced both here and on the backend. */}
          <Route
            path="/admin/raffle"
            element={
              <AdminGuard>
                <Raffle />
              </AdminGuard>
            }
          />
          <Route path="*" element={<Placeholder label="Page not found" />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

/** Post-event route guard (issue #57): once the event status is 'completed',
 *  the season pages (RSVP, My RSVP, schedule, golf, maps, raffle) are hidden —
 *  direct visits bounce to the wrapped Home. Home and Gallery stay up. UX
 *  only, like the auth guards — the backend enforces its own write locks. */
function SeasonGuard({ children }: { children: JSX.Element }) {
  const [wrapped, setWrapped] = useState<boolean | null>(null);
  useEffect(() => {
    fetch('/api/public/event?year=2026')
      .then(r => (r.ok ? r.json() : null))
      .then(ev => setWrapped(ev?.status === 'completed'))
      .catch(() => setWrapped(false));
  }, []);
  if (wrapped === null) return null; // blank beat while the event doc loads (edge-cached 5 min)
  if (wrapped) return <Navigate to="/" replace />;
  return children;
}

function Placeholder({ label }: { label: string }) {
  return (
    <section className="section">
      <div className="container">
        <span className="eyebrow">— Coming Soon</span>
        <h1>{label}</h1>
        <p style={{ color: 'var(--mid)' }}>This page hasn't been built yet.</p>
      </div>
    </section>
  );
}
